import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, ImagePlus } from "lucide-react";

/**
 * FileDropZone — wraps any region so users can drag-and-drop files into it.
 *
 * Features:
 *  - Visual highlight (copper outline + faint wash) while files hover.
 *  - Robust dragenter/dragleave counter so the highlight only clears once the
 *    cursor truly leaves the zone (HTML5 fires dragleave for every nested
 *    child, so a simple boolean flag would flicker).
 *  - Filters dropped files by `accept` (mime prefixes accepted, e.g. "image/").
 *  - Ignores text/URL drags (we only care about File entries).
 *  - Disabled mode (used while an upload is already in-flight).
 *
 * Props:
 *  - `onFiles(files: File[])` — called with the filtered file list.
 *  - `accept` — mime prefix, default "image/".
 *  - `disabled` — boolean.
 *  - `className` — wrapper classes.
 *  - `overlayLabel` — text shown in the drop overlay (e.g. "Drop images here").
 *  - `testid` — data-testid override.
 *  - `children` — wrapped content.
 */
const FileDropZone = ({
  onFiles,
  accept = "image/",
  disabled = false,
  className = "",
  overlayLabel = "Drop images to upload",
  testid = "file-drop-zone",
  children,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const counter = useRef(0);

  // Prevent the browser from opening dropped files outside the zone.
  useEffect(() => {
    const stop = (e) => {
      // Only intercept when there are files in the drag payload.
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
        e.preventDefault();
      }
    };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    return () => {
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
    };
  }, []);

  const hasFiles = (e) =>
    e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files");

  const onDragEnter = useCallback(
    (e) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      counter.current += 1;
      if (counter.current === 1) setIsDragging(true);
    },
    [disabled]
  );

  const onDragLeave = useCallback(
    (e) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      counter.current = Math.max(0, counter.current - 1);
      if (counter.current === 0) setIsDragging(false);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      // Keep "copy" cursor while hovering.
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    },
    [disabled]
  );

  const onDrop = useCallback(
    (e) => {
      if (disabled || !hasFiles(e)) return;
      e.preventDefault();
      counter.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files || []).filter((f) =>
        accept ? f.type.startsWith(accept) : true
      );
      if (files.length) onFiles(files);
    },
    [disabled, accept, onFiles]
  );

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={testid}
      data-dragging={isDragging || undefined}
    >
      {children}
      {isDragging && (
        <div
          className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-[#C16542]/10 backdrop-blur-[1px] border-2 border-dashed border-[#C16542] transition-opacity"
          data-testid={`${testid}-overlay`}
          aria-hidden="true"
        >
          <div className="flex flex-col items-center gap-2 px-6 py-5 bg-[#FDFBF7] border border-[#C16542]/40 shadow-lg">
            <ImagePlus className="w-7 h-7 text-[#C16542]" strokeWidth={1.5} />
            <p className="text-xs tracking-[0.3em] uppercase text-[#C16542]">{overlayLabel}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Lightweight inline upload-progress indicator used by both admin pages.
 * Shows "Uploading N of M…" + a thin progress bar.
 */
export const UploadProgress = ({ total, done, label = "Uploading" }) => {
  if (!total) return null;
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <div
      className="inline-flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[#5C5248]"
      data-testid="upload-progress"
    >
      <Upload className="w-3.5 h-3.5 text-[#C16542] animate-pulse" strokeWidth={1.6} />
      <span data-testid="upload-progress-text">
        {label} {Math.min(done + 1, total)} / {total}
      </span>
      <span className="relative inline-block w-32 h-1 bg-[#2C2621]/10 overflow-hidden">
        <span
          className="absolute inset-y-0 left-0 bg-[#C16542] transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
};

export default FileDropZone;
